import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ApiService } from '../services/api';
import { Area, FiltrosServicio, Servicio, Proyecto } from '../types';
import AreaCard from './AreaCard';
import ServicioCard from './ServicioCard';
import ProyectoCard from './ProyectoCard';
import FilterBar from './FilterBar';
import LoadingSpinner from './LoadingSpinner';
import ErrorMessage from './ErrorMessage';
import AreaDetailModal from './AreaDetailModal';
import UserProfile from './UserProfile';
import ThemeToggle from './ThemeToggle';
import Home from './Home';
import { Search, Grid, List } from 'lucide-react';
import PowerBIReports from './PowerBIReports';

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filtros, setFiltros] = useState<FiltrosServicio>({});
  const [navigationMessage, setNavigationMessage] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<'home' | 'areas' | 'servicios' | 'reportes' | 'proyectos'>(() => {
    const savedViewMode = localStorage.getItem('consultoraHub_dashboardViewMode');
    const validModes: ('home' | 'areas' | 'servicios' | 'reportes' | 'proyectos')[] = ['home', 'areas', 'servicios', 'reportes', 'proyectos'];
    if (savedViewMode && validModes.includes(savedViewMode as any)) {
      return savedViewMode as 'home' | 'areas' | 'servicios' | 'reportes' | 'proyectos';
    }
    return 'home';
  });

  const [layoutMode, setLayoutMode] = useState<'grid' | 'list'>(() => {
    const savedLayoutMode = localStorage.getItem('consultoraHub_dashboardLayoutMode');
    const validLayouts: ('grid' | 'list')[] = ['grid', 'list'];
    if (savedLayoutMode && validLayouts.includes(savedLayoutMode as any)) {
      return savedLayoutMode as 'grid' | 'list';
    }
    return 'grid';
  });

  useEffect(() => {
    localStorage.setItem('consultoraHub_dashboardViewMode', viewMode);
  }, [viewMode]);

  useEffect(() => {
    localStorage.setItem('consultoraHub_dashboardLayoutMode', layoutMode);
  }, [layoutMode]);

  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [areas, setAreas] = useState<Area[] | null>(null);
  const [areasLoading, setAreasLoading] = useState(false);
  const [areasError, setAreasError] = useState<string | null>(null);

  const [servicios, setServicios] = useState<Servicio[] | null>(null);
  const [serviciosLoading, setServiciosLoading] = useState(false);
  const [serviciosError, setServiciosError] = useState<string | null>(null);

  const [proyectos, setProyectos] = useState<Proyecto[] | null>(null);
  const [proyectosLoading, setProyectosLoading] = useState(false);
  const [proyectosError, setProyectosError] = useState<string | null>(null);

  const [areasLoaded, setAreasLoaded] = useState(false);
  const [serviciosLoaded, setServiciosLoaded] = useState(false);
  const [proyectosLoaded, setProyectosLoaded] = useState(false);

  const loadAreas = useCallback(async () => {
    if (areasLoading || areasLoaded) return;

    setAreasLoading(true);
    setAreasError(null);
    try {
      const data = await ApiService.getAreas();
      setAreas(data);
      setAreasLoaded(true);
    } catch (error: any) {
      setAreasError(error.message || 'Error cargando áreas');
    } finally {
      setAreasLoading(false);
    }
  }, [areasLoading, areasLoaded]);

  const loadServicios = useCallback(async () => {
    if (serviciosLoading || serviciosLoaded) return;

    setServiciosLoading(true);
    setServiciosError(null);
    try {
      const data = await ApiService.getServicios({ ...filtros });
      setServicios(data);
      setServiciosLoaded(true);
    } catch (error: any) {
      setServiciosError(error.message || 'Error cargando servicios');
    } finally {
      setServiciosLoading(false);
    }
  }, [serviciosLoading, serviciosLoaded, filtros]);

  const loadProyectos = useCallback(async () => {
    if (proyectosLoading || proyectosLoaded) return;

    setProyectosLoading(true);
    setProyectosError(null);
    try {
      const data = await ApiService.getProyectos();
      setProyectos(data);
      setProyectosLoaded(true);
    } catch (error: any) {
      setProyectosError(error.message || 'Error cargando proyectos');
    } finally {
      setProyectosLoading(false);
    }
  }, [proyectosLoading, proyectosLoaded]);

  useEffect(() => {
    if ((viewMode === 'areas' || viewMode === 'servicios') && !areasLoaded) {
      loadAreas();
    }
  }, [viewMode, areasLoaded, loadAreas]);

  useEffect(() => {
    if (viewMode === 'servicios' && !serviciosLoaded) {
      loadServicios();
    }
  }, [viewMode, serviciosLoaded, loadServicios]);

  useEffect(() => {
    if (viewMode === 'proyectos' && !proyectosLoaded) {
      loadProyectos();
    }
  }, [viewMode, proyectosLoaded, loadProyectos]);

  const filteredAreas = areas?.filter(area =>
    area.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    area.descripcion?.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const filteredServicios = servicios?.filter(servicio => {
    const matchesSearch = !searchTerm || (
      servicio.tecnologia.toLowerCase().includes(searchTerm.toLowerCase()) ||
      servicio.categoria?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      servicio.casosUso?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const matchesCategory = !filtros.categoria || servicio.categoria === filtros.categoria;
    const matchesArea = !filtros.areaId || servicio.unidadPrimaria.id === filtros.areaId;

    return matchesSearch && matchesCategory && matchesArea;
  }) || [];

  const filteredProyectos = proyectos?.filter(proyecto =>
    proyecto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proyecto.descripcion?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proyecto.clienteEmpresa?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    proyecto.area.nombre.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleFilterChange = (newFiltros: Partial<FiltrosServicio>) => {
    setFiltros(prev => ({ ...prev, ...newFiltros }));
  };

  const handleAreaClick = (area: Area) => {
    navigate(`/area/${area.id}?name=${encodeURIComponent(area.nombre)}`);
    setNavigationMessage(`Navegando a: ${area.nombre}`);
    setTimeout(() => setNavigationMessage(null), 3000);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedArea(null);
  };

  const renderContent = () => {
    const isLoading = areasLoading || serviciosLoading || proyectosLoading;
    const error = areasError || serviciosError || proyectosError;

    if (isLoading) return <LoadingSpinner />;
    if (error) return <ErrorMessage message={error} />;

    switch (viewMode) {
      case 'servicios':
        return (
          <div className={layoutMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 fade-in"
            : "space-y-4 fade-in"
          }>
            {filteredServicios.map((servicio) => (
              <ServicioCard key={servicio.id} servicio={servicio} layoutMode={layoutMode} />
            ))}
          </div>
        );
      case 'areas':
        return (
          <div className={layoutMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 fade-in"
            : "space-y-4 fade-in"
          }>
            {filteredAreas.map((area) => (
              <AreaCard
                key={area.id}
                area={area}
                layoutMode={layoutMode}
                onClick={() => handleAreaClick(area)}
              />
            ))}
          </div>
        );
      case 'proyectos':
        return (
          <div className={layoutMode === 'grid'
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 fade-in"
            : "space-y-4 fade-in"
          }>
            {filteredProyectos.map((proyecto) => (
              <ProyectoCard
                key={proyecto.id}
                proyecto={proyecto}
                layoutMode={layoutMode}
              />
            ))}
          </div>
        );
      case 'reportes':
        return <PowerBIReports searchTerm={searchTerm} />;
      case 'home':
        return <Home onNavigateToArea={(areaName) => {
          setViewMode('areas');
          localStorage.setItem('consultoraHub_dashboardViewMode', 'areas');

          const specificFilters: { [key: string]: { searchTerm: string, areaId?: number } } = {
            'empowering-humans': { searchTerm: 'Empowering Humans' },
            'secure': { searchTerm: 'Secure' },
            'cloud': { searchTerm: 'Cloud' },
            'service-delivery': { searchTerm: 'Service Delivery' },
            'change-management': { searchTerm: 'Change Management' },
            'quality-compliance': { searchTerm: 'Quality' },
            'outsourcing': { searchTerm: 'Outsourcing' },
            'data': { searchTerm: 'Data' },
            'low-code': { searchTerm: 'Low Code' },
            'pro-code': { searchTerm: 'Pro Code' },
            'demand-hub-eh': { searchTerm: 'Demand Hub' },
            'secure-service': { searchTerm: 'Security' },
            'demand-hub-secure': { searchTerm: 'Demand Hub' },
            'azure-platform': { searchTerm: 'Azure' },
            'modern-work': { searchTerm: 'Modern Work' },
            'demand-hub-cloud': { searchTerm: 'Demand Hub' }
          };

          const specificFilter = specificFilters[areaName];
          if (specificFilter) {
            setSearchTerm(specificFilter.searchTerm);
            if (specificFilter.areaId) {
              setFiltros({ areaId: specificFilter.areaId });
            } else {
              setFiltros({});
            }
          } else {
            setFiltros({});
            setSearchTerm('');
          }

          const areaNames: { [key: string]: string } = {
            'empowering-humans': 'Empowering Humans',
            'secure': 'Secure',
            'cloud': 'Cloud',
            'service-delivery': 'Service Delivery',
            'change-management': 'Change Management',
            'quality-compliance': 'Quality & Compliance',
            'outsourcing': 'Outsourcing',
            'data': 'Data Services',
            'low-code': 'Low Code Platform',
            'pro-code': 'Pro Code Development',
            'demand-hub-eh': 'Demand Hub - Empowering',
            'secure-service': 'Security Services',
            'demand-hub-secure': 'Demand Hub - Security',
            'azure-platform': 'Azure Platform',
            'modern-work': 'Modern Work',
            'demand-hub-cloud': 'Demand Hub - Cloud'
          };

          const displayName = areaNames[areaName] || areaName;
          setNavigationMessage(`Mostrando: ${displayName}`);
          setTimeout(() => setNavigationMessage(null), 3000);
        }} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 transition-theme">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white dark:bg-neutral-900 border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            {/* Logo */}
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary dark:bg-primary-dark rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-lg">RH</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-neutral-900 dark:text-neutral-50">
                  ReadyHub
                </h1>
                <p className="text-xs text-neutral-500 dark:text-neutral-400">
                  Enterprise Platform
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              {/* Navigation Tabs */}
              <nav className="flex items-center gap-1">
                {[
                  { key: 'home', label: 'Home' },
                  { key: 'servicios', label: 'Servicios' },
                  { key: 'areas', label: 'Áreas' },
                  { key: 'proyectos', label: 'Proyectos' },
                  { key: 'reportes', label: 'Reportes' }
                ].map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => setViewMode(tab.key as any)}
                    className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === tab.key
                        ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50'
                        : 'text-neutral-600 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                    }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>

              {/* Layout Toggle */}
              <div className="flex gap-1">
                <button
                  onClick={() => setLayoutMode('grid')}
                  className={`p-2 rounded-md transition-all ${
                    layoutMode === 'grid'
                      ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50'
                      : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                  }`}
                >
                  <Grid size={18} />
                </button>
                <button
                  onClick={() => setLayoutMode('list')}
                  className={`p-2 rounded-md transition-all ${
                    layoutMode === 'list'
                      ? 'bg-neutral-100 dark:bg-neutral-800 text-neutral-900 dark:text-neutral-50'
                      : 'text-neutral-500 dark:text-neutral-400 hover:bg-neutral-50 dark:hover:bg-neutral-800'
                  }`}
                >
                  <List size={18} />
                </button>
              </div>

              {/* Theme Toggle */}
              <ThemeToggle />

              {/* User Profile */}
              <UserProfile onNavigateToConfig={() => navigate('/configuration')} />
            </div>
          </div>
        </div>
      </header>

      {/* Navigation Message */}
      {navigationMessage && (
        <div className="fixed top-20 right-4 z-50 fade-in">
          <div className="card p-4 shadow-md">
            <p className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
              {navigationMessage}
            </p>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      {viewMode !== 'home' && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
          <div className="card p-4 mb-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-neutral-400 dark:text-neutral-500" size={20} />
                  <input
                    type="text"
                    placeholder={`Buscar ${viewMode}...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="input-field pl-10"
                  />
                </div>
              </div>

              {/* Filter Bar */}
              {viewMode === 'servicios' && (
                <div className="flex-none">
                  <FilterBar
                    areas={areas || []}
                    onFilterChange={handleFilterChange}
                    currentFilters={filtros}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className={viewMode !== 'home' ? "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12" : ""}>
        {renderContent()}
      </div>

      {/* Area Detail Modal */}
      {selectedArea && (
        <AreaDetailModal
          area={selectedArea}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default Dashboard;
